/**
 * NX-2745 (E8.15) — the org-wide delivery calendar: a month grid over the roadmap read-model.
 *
 * Pure presentation over ALREADY-derived data (AC1): deliveries come from /roadmap (E7.10) and the
 * RAG colour of each delivery is the project's OWN portfolio status, joined client-side by project
 * id — the same RagDot/ConfidencePill rendering the Portfolio screen established, never a second
 * colour scale invented from confidence thresholds (AC2).
 *
 * The three delivery shapes, honestly (the design decision the plan calls out):
 *  - dated (declared target or supported forecast) → plots on its day cell (AC3);
 *  - unsupported forecast WITH a declared date → plots on that date, explicitly marked
 *    "unsupported"; with NO date at all → listed in the sidebar's unsupported bucket — never
 *    silently omitted, never a fabricated date (AC3);
 *  - continuous mode → never a day cell; a continuous team has no single delivery date, so it
 *    lives in the persistent sidebar with its cadence summary (AC4);
 *  - deliveryMode null (unprofiled) → sidebar "unprofiled" bucket, visible rather than mistaken
 *    for "nothing due" (AC5).
 *
 * Month navigation re-fetches the roadmap (AC6) — the month rides along as a cache-busting query
 * param the server tolerates; the server's horizon stays the source of truth, and a month beyond
 * the fetched rest-of-year window says so instead of claiming an empty month.
 */
import { useState } from "react";
import { Breadcrumb } from "../shell/Shell.js";
import { useApi } from "../api/useApi.js";
import { ConfidencePill, EmptyState, RagDot, type Rag } from "../components.js";
import { Button } from "../components/controls.js";
import { type RoadmapData, type RoadmapEntry } from "../api/useRoadmap.js";
import "./calendar.css";

interface PortfolioStatusRow {
  readonly id: string;
  readonly status: string;
}

const STATUS_RAG: Record<string, Rag> = {
  green: "green",
  yellow: "amber",
  red: "red",
};

interface Delivery {
  readonly entry: RoadmapEntry;
  readonly date: string; // YYYY-MM-DD
  readonly unsupported: boolean;
  readonly confidence: number | null;
}

/** The date a delivery plots on: the declared target first, else the supported forecast's p50. */
function deliveryDate(e: RoadmapEntry): string | null {
  if (e.targetDate !== null) return e.targetDate;
  if (e.forecast.kind === "value") return e.forecast.value.p50Date.slice(0, 10);
  return null;
}

function classify(entries: readonly RoadmapEntry[]): {
  dated: Delivery[];
  continuous: RoadmapEntry[];
  unprofiled: RoadmapEntry[];
  undatedUnsupported: RoadmapEntry[];
} {
  const dated: Delivery[] = [];
  const continuous: RoadmapEntry[] = [];
  const unprofiled: RoadmapEntry[] = [];
  const undatedUnsupported: RoadmapEntry[] = [];
  for (const e of entries) {
    if (e.deliveryMode === "continuous") {
      continuous.push(e);
      continue;
    }
    if (e.deliveryMode === null) {
      unprofiled.push(e);
      continue;
    }
    const date = deliveryDate(e);
    if (date === null) {
      undatedUnsupported.push(e);
      continue;
    }
    dated.push({
      entry: e,
      date,
      unsupported: e.forecast.kind !== "value",
      confidence:
        e.forecast.kind === "value" && e.forecast.confidence.kind === "value"
          ? e.forecast.confidence.confidence
          : null,
    });
  }
  return { dated, continuous, unprofiled, undatedUnsupported };
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const monthKey = (y: number, m: number): string => `${y}-${String(m + 1).padStart(2, "0")}`;

export function Calendar(): React.ReactNode {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth()); // 0-based
  // NX-2841 — fetch WITHOUT a horizon: a horizoned /roadmap excludes unsupported entries without
  // declared dates, which would starve the sidebar buckets (including continuous/unprofiled).
  // The calendar windows by month itself; the month param re-keys the fetch (AC6 re-fetch).
  const roadmap = useApi<RoadmapData>(`/roadmap?limit=100&month=${monthKey(year, month)}`);
  const portfolio = useApi<PortfolioStatusRow[]>(`/portfolio?limit=100`);
  const crumb = <Breadcrumb items={[{ label: "Calendar" }]} />;

  if (roadmap.status === "loading" || portfolio.status === "loading") {
    return (
      <>
        {crumb}
        <div className="meta">Loading deliveries from the evidence store…</div>
      </>
    );
  }
  if (roadmap.status === "error") {
    return (
      <>
        {crumb}
        <EmptyState message={`Couldn't load the roadmap: ${roadmap.error.message}`} />
      </>
    );
  }
  // Round-1 review finding: a failed portfolio fetch must NOT degrade into neutral dots that
  // pretend to be project status — that is invented data. When it fails, the dots are withheld
  // and the failure is stated.
  const portfolioFailed = portfolio.status === "error";
  const statusOf = new Map<string, string>(
    portfolio.status === "ready" ? portfolio.envelope.data.map((p) => [p.id, p.status]) : [],
  );
  const { dated, continuous, unprofiled, undatedUnsupported } = classify(
    roadmap.envelope.data.entries,
  );

  const first = new Date(Date.UTC(year, month, 1));
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const leading = first.getUTCDay(); // 0=Sun
  const prefix = monthKey(year, month);
  const byDay = new Map<number, Delivery[]>();
  for (const d of dated) {
    if (!d.date.startsWith(prefix)) continue;
    const day = Number(d.date.slice(8, 10));
    byDay.set(day, [...(byDay.get(day) ?? []), d]);
  }

  const step = (delta: number): void => {
    const next = new Date(Date.UTC(year, month + delta, 1));
    setYear(next.getUTCFullYear());
    setMonth(next.getUTCMonth());
  };

  return (
    <>
      {crumb}
      <div className="head">
        <h1>Calendar</h1>
      </div>
      <div className="calnav">
        <Button size="sm" variant="ghost" onClick={() => step(-1)}>
          ← Prev
        </Button>
        <h2>
          {MONTH_NAMES[month]} {year}
        </h2>
        <Button size="sm" variant="ghost" onClick={() => step(1)}>
          Next →
        </Button>
        <span className="cnt">
          org-wide deliveries · colours are each project's own portfolio status
        </span>
      </div>
      <div className="callayout">
        <div
          className="calgrid"
          role="grid"
          aria-label={`${MONTH_NAMES[month]} ${year} deliveries`}
        >
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div className="caldow" key={d}>
              {d}
            </div>
          ))}
          {Array.from({ length: leading }, (_, i) => (
            <div className="calcell pad" key={`pad-${i}`} aria-hidden="true" />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const items = byDay.get(day) ?? [];
            return (
              <div className="calcell" key={day} role="gridcell">
                <span className="d">{day}</span>
                {items.map((d) => (
                  <span
                    className={`caldel${d.unsupported ? " unsupported" : ""}`}
                    key={d.entry.id}
                    title={d.entry.name}
                  >
                    {!portfolioFailed && (
                      <RagDot
                        status={STATUS_RAG[statusOf.get(d.entry.id) ?? ""] ?? "neutral"}
                        label={`${d.entry.name} status`}
                      />
                    )}
                    <span className="nm">{d.entry.name}</span>
                    {d.unsupported ? (
                      <small>unsupported forecast</small>
                    ) : (
                      <ConfidencePill value={d.confidence} />
                    )}
                  </span>
                ))}
              </div>
            );
          })}
        </div>
        <aside className="calside">
          <h3 className="sec">Continuous delivery</h3>
          <p className="note">
            These teams deliver by cadence, not a date — a calendar cell would misstate how they
            work.
          </p>
          {continuous.length === 0 && <p className="note">None in this portfolio.</p>}
          {continuous.map((e) => (
            <div className="calsideitem" key={e.id}>
              {!portfolioFailed && (
                <RagDot
                  status={STATUS_RAG[statusOf.get(e.id) ?? ""] ?? "neutral"}
                  label={`${e.name} status`}
                />
              )}
              <a href={`/projects/${e.id}`}>{e.name}</a>
              <small>continuous · see project flow metrics</small>
            </div>
          ))}
          {undatedUnsupported.length > 0 && (
            <>
              <h3 className="sec">No honest date</h3>
              <p className="note">Forecast unsupported and no declared target — not plotted.</p>
              {undatedUnsupported.map((e) => (
                <div className="calsideitem" key={e.id}>
                  <a href={`/projects/${e.id}`}>{e.name}</a>
                  <small>
                    {e.forecast.kind === "n/a" ? e.forecast.reason : "no date available"}
                  </small>
                </div>
              ))}
            </>
          )}
          {unprofiled.length > 0 && (
            <>
              <h3 className="sec">Unprofiled</h3>
              <p className="note">
                No declared delivery mode — absent from the grid by honesty, not because nothing is
                due.
              </p>
              {unprofiled.map((e) => (
                <div className="calsideitem" key={e.id}>
                  <a href={`/projects/${e.id}`}>{e.name}</a>
                </div>
              ))}
            </>
          )}
        </aside>
      </div>
      {portfolioFailed && (
        <p className="note">
          Project status colours are unavailable right now (portfolio request failed:{" "}
          {portfolio.error.message}) — deliveries render without status dots rather than with an
          invented neutral.
        </p>
      )}
      <p className="note">
        An empty day means no delivery is currently declared or forecast for it. Forecast dates are
        near-term by nature — far-future months empty out honestly rather than being projected.
      </p>
    </>
  );
}
